import { FC, useCallback, useState } from 'react'

import { useQuery } from 'react-query'

import AmountInput from '@/components/base/AmountInput'
import BaseDialog from '@/components/base/BaseDialog'
import BaseButton from '@/components/base/Button'
import { useStake } from '@/root/src/hooks/use-stake'
import { useTokenBalance } from '@/root/src/hooks/use-token-balance'
import { fetchSetting } from '@/root/src/utils/apis/setting'
import { format, formatPercentage } from '@/root/src/utils/number-helpers'
import { calculateEstDailyReward } from '@/root/src/utils/staking-helpers'
import { secondsToDays } from '@/root/src/utils/time-helpers'
import { Box, Divider, Stack, Typography, useMediaQuery, useTheme } from '@mui/material'
import { useWallet } from '@solana/wallet-adapter-react'
import ConnectWallet from '../connect-wallet'
import IneligibleLCRatio from '../base/IneligibleLCRatio'
import { isSolcatToken } from '../../utils/helpers'
import { useProtocolData } from '../../hooks/use-protocol-data'
import { useRouter } from 'next/router'
import JitoValidatorSwitch from '../base/JitoValidatorSwitch'

const mintTextDetails = ['Est APR', 'Est daily reward', 'LC earning']

type DialogProps = {
  showModal: boolean
  onClose: () => void
  tokenAvatar: string
  tokenSymbol: string
  tokenAddress: string
}

const FlexibleAPYDialog: FC<DialogProps> = ({ showModal, onClose, tokenAvatar, tokenSymbol, tokenAddress }) => {
  const [amount, setAmount] = useState('')
  const { connected } = useWallet()
  const router = useRouter()
  const theme = useTheme()
  const isSmall = useMediaQuery(theme.breakpoints.up('sm'))

  const { data: tokenBalance } = useTokenBalance(tokenAddress)

  // const { data: stakingInfo } = useStakingInfo(tokenAddress)
  const { data: protocolData } = useProtocolData([tokenAddress])
  const stakingInfo = protocolData?.stakingInfo[tokenAddress]

  const flexibleApr = stakingInfo?.flexibleApr
  const flexibleWithdrawalDelayTime = stakingInfo?.flexibleWithdrawalDelayTime

  const withdrawDelayDays = secondsToDays(Number(flexibleWithdrawalDelayTime) || 0)

  const { data: settingData } = useQuery({
    queryKey: ['setting'],
    queryFn: () => fetchSetting(),
  })
  const isSolcat = isSolcatToken(tokenSymbol)
  const lcRatio = isSolcat ? settingData?.stake_lc_ratio_json?.flexible : 0
  const lcEarning = amount ? Number(lcRatio || 0) * Number(amount) : 0
  const formattedLcRatio = lcRatio + 'x'
  const formattedLcEarning = format(lcEarning, true, 0)
  const estDailyReward = calculateEstDailyReward(flexibleApr?.toString() || '0', amount || '0')
  const stakeDetails = [
    {
      name: 'Est APR',
      value: `${formatPercentage(flexibleApr?.toString() || '0', false, 2)}`,
    },
    { name: 'Unstake delay time', value: `${withdrawDelayDays} days` },
    { name: 'LC ratio', value: formattedLcRatio },
    ...(isSolcat ? [{ name: 'LC earning', value: formattedLcEarning }] : []),
    {
      name: 'Est daily reward',
      value: `${estDailyReward} ${tokenSymbol}`,
    },
  ]

  const { mutateAsync: stake, isLoading: isLoadingStake } = useStake()

  const handleCloseModal = useCallback(() => {
    onClose()
    setAmount('')
    document.body.style.overflow = 'auto'
  }, [onClose])

  const handleStake = async () => {
    try {
      await stake([tokenAddress, amount])
      router.push('/portfolio')
    } catch (error) {
      console.error('Error during staking:', error)
    } finally {
      handleCloseModal()
    }
  }

  return (
    <BaseDialog
      handleClose={handleCloseModal}
      isOpened={showModal}
      title="Flexible staking"
      body={
        <Stack>
          <AmountInput
            amount={amount}
            setAmount={setAmount}
            balance={tokenBalance}
            tokenAvatar={tokenAvatar}
            tokenSymbol={tokenSymbol}
          />
          <Divider
            className="w-full mt-5"
            style={{
              height: '2px',
              backgroundColor: 'rgba(255, 255, 255, 0.08)',
            }}
          />
          <Stack className="mt-5 px-1">
            {stakeDetails.map((detail, index) => {
              if (!isSolcat && detail.name === 'LC ratio') return <IneligibleLCRatio key={index} />
              return (
                <Stack key={index} direction="row" justifyContent="space-between" className="py-1.5 lg:py-2">
                  <Typography className="text-white/50 text-xs-2 sm:text-sm font-dm_mono_regular">
                    {detail.name}
                  </Typography>
                  <Typography
                    className={`${
                      mintTextDetails.includes(detail.name) ? 'text-mint-green' : 'text-white'
                    } text-xs-2 sm:text-sm font-dm_mono_regular`}
                  >
                    {detail.value}
                  </Typography>
                </Stack>
              )
            })}
            {connected && <JitoValidatorSwitch />}
          </Stack>
          <Divider
            className="w-full my-5"
            style={{
              height: '2px',
              backgroundColor: 'rgba(255, 255, 255, 0.08)',
            }}
          />
          {!connected ? (
            <Box onClick={() => handleCloseModal()}>
              <ConnectWallet />
            </Box>
          ) : (
            <BaseButton
              disabled={!connected || !amount || Number(amount) > Number(tokenBalance)}
              customVariant="emphasis"
              fontSize={isSmall ? 'base' : 'sm'}
              onClick={handleStake}
              isLoading={isLoadingStake}
              className="px-6 py-3"
            >
              Stake
            </BaseButton>
          )}
        </Stack>
      }
    />
  )
}

export default FlexibleAPYDialog
